<template>
  <div class="col-xl-3">
    <div class="auth-full-page-content p-md-5 p-4">
      <div class="w-100">
        <div class="d-flex flex-column h-100">
          <login-header></login-header>
          <div class="my-auto">
            <div>
              <h5 class="text-primary">{{ $t('pages.login.register_account') }}</h5>
              <p class="text-muted">{{ $t('pages.login.get_app_account') }}</p>
            </div>

            <div class="mt-4">
              <b-alert
                  v-model="registerSuccess"
                  class="mt-3"
                  variant="success"
                  dismissible
              >{{ $t('pages.login.registration_success') }}
              </b-alert
              >

              <validation-error v-if="isRegisterError" :error="regError"/>

              <b-form @submit.prevent="register">
                <b-form-group
                    class="mb-3"
                    id="business-name-group"
                    :label="$t('pages.login.business_name')"
                    label-for="business-name"
                >
                  <b-form-input
                      id="business-name"
                      v-model="user.business_name"
                      type="text"
                      :placeholder="$t('pages.login.enter_business_name')"
                      :class="{'is-invalid': submitted && $v.user.business_name.$error}"
                  ></b-form-input>
                  <div
                      v-if="submitted && $v.user.business_name.$error"
                      class="invalid-feedback"
                  >
                      <span v-if="!$v.user.business_name.required">
                    {{ $t('pages.login.validations.business_name_required') }}
                      </span>
                    <span v-if="!$v.user.business_name.minLength">
                        {{
                        $t('pages.login.validations.business_name_min', {min: $v.user.business_name.$params.minLength.min})
                      }}
                      </span>
                    <span v-if="!$v.user.business_name.maxLength">
                        {{
                        $t('pages.login.validations.business_name_max', {max: $v.user.business_name.$params.maxLength.max})
                      }}
                      </span>
                  </div>
                </b-form-group>

                <b-form-group
                    class="mb-3"
                    id="fullname-group"
                    :label="$t('pages.login.email')"
                    label-for="email"
                >
                  <b-form-input
                      id="email"
                      v-model="user.email"
                      type="email"
                      :placeholder="$t('pages.login.enter_email')"
                      :class="{'is-invalid': submitted && $v.user.email.$error}"
                  >
                  </b-form-input>
                  <div
                      v-if="submitted && $v.user.email.$error"
                      class="invalid-feedback"
                  >
                    <span v-if="!$v.user.email.required">
                      {{ $t('pages.login.validations.email_required') }}
                    </span>
                    <span v-if="!$v.user.email.email">
                      {{ $t('pages.login.validations.email_invalid') }}
                    </span>
                  </div>
                </b-form-group>

                <b-form-group
                    class="mb-3"
                    id="password-group"
                    label="Password"
                    label-for="password"
                >
                  <b-form-input
                      id="password"
                      v-model="user.password"
                      type="password"
                      :placeholder="$t('pages.login.enter_password')"
                      :class="{'is-invalid': submitted && $v.user.password.$error}"
                  ></b-form-input>
                  <div
                      v-if="submitted && $v.user.password.$error"
                      class="invalid-feedback"
                  >
                      <span v-if="!$v.user.password.required">
                    {{ $t('pages.login.validations.password_required') }}
                      </span>
                    <span v-if="!$v.user.password.minLength">
                        {{
                        $t('pages.login.validations.password_min', {min: $v.user.password.$params.minLength.min})
                      }}
                      </span>
                    <span v-if="!$v.user.password.maxLength">
                        {{
                        $t('pages.login.validations.password_max', {max: $v.user.password.$params.maxLength.max})
                      }}
                      </span>
                  </div>
                </b-form-group>

                <div class="mt-4 d-grid">
                  <b-button type="submit" variant="primary" class="btn-block" :disabled="loading">
                    <b-spinner v-if="loading" small></b-spinner>
                    {{ $t('pages.login.register') }}
                  </b-button
                  >
                </div>

              </b-form>

              <div class="mt-5 text-center">
                <p>
                  {{ $t('pages.login.has_account') }}
                  <router-link
                      to="/login"
                      class="fw-medium text-primary"
                  >
                    {{ $t('pages.login.login') }}
                  </router-link
                  >
                </p>
              </div>
            </div>
          </div>
          <login-footer></login-footer>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {maxLength, minLength, required, email} from "vuelidate/lib/validators";
import LoginFooter from "./LoginFooter";
import LoginHeader from "./LoginHeader";
import * as ls from 'local-storage'
import ValidationError from "../../../../components/widgets/ValidationError";

export default {
  name: "RegisterForm",
  page() {
    return {
      title: this.$t('pages.login.register'),
      meta: [
        {
          name: "description",
          content: this.$t('common.app_title')
        }
      ]
    }
  },
  components: {ValidationError, LoginHeader, LoginFooter},
  data() {
    return {
      user: {
        email: "",
        password: "",
        phone_number: "",
        business_name: "",
      },
      loading: false,
      submitted: false,
      regError: null,
      tryingToRegister: false,
      isRegisterError: false,
      registerSuccess: false,
      translations: {
        countrySelectorLabel: this.$t('pages.login.country_code'),
        countrySelectorError: this.$t('pages.login.choose_country'),
        phoneNumberLabel: this.$t('pages.login.phone_number'),
        example: this.$t('common.example'),
      }
    };
  },
  validations: {
    user: {
      email: {
        required,
        email
      },
      password: {
        required,
        minLength: minLength(6),
        maxLength: maxLength(25)
      },
      business_name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(180)
      }
    },
  },
  methods: {
    async register() {
      this.submitted = true;

      // stop here if form is invalid
      this.$v.$touch();

      if (this.$v.$invalid) {
        return
      } else {
        this.tryingToLogIn = true;
        // Reset the authError if it existed.
        this.authError = null;

        const vm = this

        vm.loading = true
        await vm.$auth.register({
          data: {
            email: vm.user.email,
            password: vm.user.password,
            business_name: vm.user.business_name,
          },
        }).then(async function () {
          vm.handleUserPlan()
          await vm.$router.push({name: 'email-verification'})
        }).catch(err => {
          vm.tryingToRegister = false;
          vm.regError = err.data
          vm.isRegisterError = true;
        }).finally(() => {
          vm.loading = false
        })
      }
    },
    handleUserPlan() {
      const plan = this.$route.query?.plan
      if (plan) {
        ls.set('selectedPlan', plan)
      }
      ls.set('userJustArrived', true)
    }
  }
}
</script>
